import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { PageHeader } from '../components/PageHeader';
import { TextArea } from '../components/Field';
import { PhotoInput, type UploadedItem } from '../components/PhotoInput';
import { StatusPill } from '../components/StatusPill';
import { Toast, type ToastState } from '../components/Toast';
import {
  api,
  ApiError,
  CATEGORY_LABELS,
  type TicketMessage,
} from '../lib/api';
import { formatTime } from '../lib/format';
import { peekLauncherInitData, useWebAppContext } from '../lib/maxWebApp';
import { supportHomePath } from '../lib/supportNav';

export function TicketPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [toast, setToast] = useState<ToastState | null>(null);
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<UploadedItem[]>([]);

  const { authReady, waitingForMax, authError, handoffPending } = useWebAppContext();
  const launcherInitData = peekLauncherInitData();
  const canCallApi =
    !handoffPending && (authReady || Boolean(launcherInitData));

  const query = useQuery({
    // Fingerprint so a failed pre-auth fetch is not stuck after handoff completes.
    queryKey: ['ticket', id, canCallApi, launcherInitData.slice(0, 24)],
    queryFn: () => api.getTicket(id!),
    enabled: Boolean(id) && canCallApi,
    refetchInterval: 15_000,
    retry: (count, err) => {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        return count < 2;
      }
      return false;
    },
  });

  const send = useMutation({
    mutationFn: async () => {
      const trimmed = body.trim();
      if (trimmed.length < 1) throw new Error('Введите сообщение');
      return api.addMessage(id!, {
        body: trimmed,
        attachment_tokens: attachments.map((a) => a.token),
      });
    },
    onSuccess: () => {
      setBody('');
      setAttachments([]);
      qc.invalidateQueries({ queryKey: ['ticket', id] });
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Не удалось отправить';
      setToast({ kind: 'error', message: msg });
    },
  });

  if ((waitingForMax || handoffPending) && !canCallApi) {
    return (
      <main className="page-shell page-shell--ticket">
        <PageHeader
          title="Тикет"
          subtitle="Подключение…"
          leading={<BackToListButton />}
        />
        <div className="page-shell__body">
          <p className="text-center text-ink-muted py-8">Получаем данные входа…</p>
        </div>
      </main>
    );
  }

  if (authError && !canCallApi) {
    return (
      <main className="page-shell">
        <PageHeader title="Тикет" leading={<BackToListButton />} />
        <div className="page-shell__body">
          <Card title="Не удалось войти">
            <p className="text-[14px] text-ink-muted">{authError}</p>
          </Card>
        </div>
      </main>
    );
  }

  if (!canCallApi) {
    return (
      <main className="page-shell">
        <PageHeader title="Тикет" leading={<BackToListButton />} />
        <div className="page-shell__body">
          <Card title="Нет данных входа">
            <p className="text-[14px] text-ink-muted">
              Откройте тикет снова из бота (кнопка в уведомлении или «Поддержка»).
            </p>
          </Card>
        </div>
      </main>
    );
  }

  if (query.isPending) {
    return (
      <main className="page-shell page-shell--ticket">
        <PageHeader
          title="Тикет"
          subtitle="Загружаем…"
          leading={<BackToListButton />}
        />
        <div className="page-shell__body">
          <p className="text-center text-ink-muted py-8">Загружаем тикет…</p>
        </div>
      </main>
    );
  }

  if (query.isError || !query.data) {
    const err = query.error;
    const subtitle = mapTicketLoadError(err);
    return (
      <main className="page-shell">
        <PageHeader title="Тикет" leading={<BackToListButton />} />
        <div className="page-shell__body">
          <Card title="Не удалось открыть тикет">
            <p className="text-[14px] text-ink-muted">{subtitle}</p>
          </Card>
        </div>
      </main>
    );
  }

  const { ticket, messages } = query.data;
  const closed = ticket.status === 'resolved' || ticket.status === 'archived';
  const title = ticket.subject ?? CATEGORY_LABELS[ticket.category];

  return (
    <main className="page-shell page-shell--ticket">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      <PageHeader
        title={`#${ticket.id}`}
        subtitle={title}
        leading={<BackToListButton />}
      />

      <div className="page-shell__body flex-1 pb-0">
        <Card padded className="!p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[13px] text-ink-muted">{CATEGORY_LABELS[ticket.category]}</p>
            </div>
            <StatusPill status={ticket.status} />
          </div>
        </Card>

        <Card padded={false} className="flex-1">
          <div className="flex flex-col gap-3 p-4 sm:p-5">
            {messages.length === 0 ? (
              <p className="text-center text-ink-muted py-4">Пока нет сообщений.</p>
            ) : (
              messages.map((m) => <MessageBubble key={m.id} message={m} />)
            )}
          </div>
        </Card>
      </div>

      {!closed ? (
        <div className="ticket-composer">
          <div className="flex flex-col gap-3 max-w-[640px] mx-auto">
            <TextArea
              value={body}
              maxLength={1000}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Напишите ответ…"
              rows={3}
              className="min-h-[88px]"
            />
            <PhotoInput
              value={attachments}
              onChange={setAttachments}
              onError={(m) => setToast({ kind: 'error', message: m })}
            />
            <Button
              leftIcon={<Send size={16} />}
              loading={send.isPending}
              onClick={() => send.mutate()}
              fullWidth
              className="min-h-[48px] text-[16px]"
            >
              Отправить
            </Button>
          </div>
        </div>
      ) : (
        <div className="ticket-composer text-center text-[13px] text-ink-muted">
          Тикет закрыт. Если проблема повторится — создайте новое обращение.
        </div>
      )}
    </main>
  );
}

function mapTicketLoadError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.code === 'expired_auth' || err.code === 'invalid_signature') {
      return 'Сессия не подтвердилась. Откройте тикет снова из бота.';
    }
    if (err.status === 403 || err.code === 'not_owner' || err.code === 'ticket_not_found') {
      return 'Тикет не найден или нет доступа. Если видите его в списке — откройте оттуда.';
    }
    if (err.status === 404) {
      return 'Тикет не найден. Возможно, он был удалён.';
    }
    return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Не удалось загрузить тикет. Попробуйте ещё раз.';
}

function BackToListButton() {
  const location = useLocation();
  return (
    <Link to={supportHomePath(location)} className="icon-nav-btn" aria-label="К обращениям">
      <ArrowLeft size={20} />
    </Link>
  );
}

function MessageBubble({ message }: { message: TicketMessage }) {
  const isUser = message.author_kind === 'user';
  const isSystem = message.author_kind === 'system';

  if (isSystem) {
    // Может быть как короткой пометкой, так и автоответом на пару строк —
    // поэтому блок, а не «таблетка».
    return (
      <div className="flex justify-center">
        <p className="max-w-[92%] text-center text-[12px] leading-relaxed text-ink-muted bg-surface-muted border border-line rounded-xl px-3.5 py-2 whitespace-pre-wrap break-words">
          {message.body}
        </p>
      </div>
    );
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[88%] sm:max-w-[80%] rounded-2xl px-3.5 py-2.5 border ${
          isUser
            ? 'bg-accent text-white border-accent rounded-br-md'
            : 'bg-surface-muted text-ink border-line rounded-bl-md'
        }`}
      >
        <p className="text-[15px] leading-snug whitespace-pre-wrap break-words">
          {message.body}
        </p>
        {message.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {message.attachments.map((a) =>
              a.mime.startsWith('image/') ? (
                <a
                  key={a.id}
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block overflow-hidden rounded-md border border-white/20"
                >
                  <img
                    src={a.url}
                    alt="Вложение"
                    className="max-h-40 max-w-[200px] object-cover"
                  />
                </a>
              ) : (
                <span
                  key={a.id}
                  className={`text-[11px] rounded-md px-2 py-1 ${
                    isUser ? 'bg-white/15' : 'bg-white border border-line'
                  }`}
                >
                  📎 {a.mime.replace('image/', '').toUpperCase()}
                </span>
              ),
            )}
          </div>
        )}
        <div
          className={`mt-1 text-[11px] ${
            isUser ? 'text-white/60 text-right' : 'text-ink-faint'
          }`}
        >
          {formatTime(message.created_at)}
        </div>
      </div>
    </div>
  );
}
